import { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, UserPlus, Trash2, Shield, User, KeyRound, Copy, Network, Power, Pencil, Eye, Building2, Plus } from "lucide-react";
import { PeopleCheckList } from "@/components/PeopleCheckList";
import { Avatar } from "@/components/Avatar";

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const ROLE_META = {
  user: { label: "User", icon: User, cls: "bg-foreground/10 text-foreground" },
  manager: { label: "Manager", icon: Shield, cls: "bg-sky-500/10 text-sky-600" },
};

function CheckList({ options, selected, onToggle, testid }) {
  if (options.length === 0) return <p className="text-xs text-muted-foreground">No managers available yet</p>;
  return (
    <div className="space-y-1.5 max-h-40 overflow-auto rounded-lg border border-border p-2" data-testid={testid}>
      {options.map((o) => (
        <label key={o.id} className="flex items-center gap-2 text-sm cursor-pointer">
          <Checkbox checked={selected.includes(o.id)} onCheckedChange={() => onToggle(o.id)} data-testid={`${testid}-${o.id}`} />
          <span>{o.name} <span className="text-[11px] text-muted-foreground">· {(ROLE_META[o.role] || ROLE_META.manager).label}</span></span>
        </label>
      ))}
    </div>
  );
}

export default function TeamPanel() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [role, setRole] = useState("user");
  const [managerIds, setManagerIds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [resetInfo, setResetInfo] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [vieweeTarget, setVieweeTarget] = useState(null);
  const [savingViewees, setSavingViewees] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [department, setDepartment] = useState("");
  const [deptDialog, setDeptDialog] = useState(false);
  const [newDept, setNewDept] = useState("");

  const isOwner = user.is_owner;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [u, d] = await Promise.all([api.get("/users"), api.get("/departments")]);
      setUsers(u.data);
      setDepartments(d.data);
    } catch (e) {
      toast.error("Could not load users");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const managerOpts = useMemo(() => users.filter((u) => u.role === "manager"), [users]);
  const roleOptions = ["user", "manager"];
  const toggleIn = (setter) => (id) => setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const addUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { name, email, password, role, manager_ids: managerIds, department });
      toast.success(`${name} added`);
      setName(""); setEmail(""); setPassword(""); setRole("user"); setManagerIds([]); setDepartment("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to add user");
    } finally { setSaving(false); }
  };

  const saveAssign = async () => {
    try {
      await api.patch(`/users/${editUser.id}/assign`, { manager_ids: editUser.manager_ids });
      toast.success("Assignment updated");
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    }
  };

  const setDept = async (id, dept) => {
    try {
      await api.patch(`/users/${id}/department`, { department: dept === "none" ? "" : dept });
      toast.success("Department updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update department"); }
  };

  const saveDept = async () => {
    const n = newDept.trim();
    if (!n) return;
    try {
      await api.post("/departments", { name: n });
      setNewDept("");
      const { data } = await api.get("/departments");
      setDepartments(data);
      toast.success(`"${n}" department added`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const removeDept = async (id) => {
    try {
      await api.delete(`/departments/${id}`);
      const { data } = await api.get("/departments");
      setDepartments(data);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const saveViewees = async () => {
    setSavingViewees(true);
    try {
      await api.patch(`/users/${vieweeTarget.id}/viewees`, { viewee_ids: vieweeTarget.viewee_ids });
      toast.success(vieweeTarget.viewee_ids.length ? `${vieweeTarget.name} can now view ${vieweeTarget.viewee_ids.length} member(s)` : `Cleared ${vieweeTarget.name}'s report access`);
      setVieweeTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update");
    } finally { setSavingViewees(false); }
  };

  const removeUser = async (id, uname) => {
    try { await api.delete(`/users/${id}`); toast.success(`${uname} removed`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const changeRole = async (id, newRole, uname) => {
    try { await api.patch(`/users/${id}/role`, { role: newRole }); toast.success(`${uname} is now ${ROLE_META[newRole].label}`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const resetPassword = async (id, uname) => {
    try { const { data } = await api.post(`/users/${id}/reset-password`); setResetInfo({ name: uname, password: data.temp_password }); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const toggleActive = async (id, uname, isDisabled) => {
    if (!isDisabled && !(await confirm({ title: `Disable ${uname}?`, description: "They won't be able to sign in and will be excluded from counts, leaderboard and reminder emails, but their reports stay.", confirmText: "Disable", destructive: true }))) return;
    try { await api.patch(`/users/${id}/active`, { active: isDisabled }); toast.success(isDisabled ? `${uname} re-enabled` : `${uname} disabled`); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };
  const saveRename = async () => {
    const nm = renameVal.trim();
    if (nm.length < 2) { toast.error("Name must be at least 2 characters"); return; }
    try { await api.patch(`/users/${renameTarget}/name`, { name: nm }); toast.success("Name updated"); setRenameTarget(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed"); }
  };

  const assignLabel = editUser?.role === "manager"
    ? "Managers this manager reports to"
    : "Managers who see this user's reports";

  return (
    <div className={`grid gap-4 md:gap-6 ${isOwner ? "lg:grid-cols-3" : ""}`}>
      {isOwner && (
        <Card className="p-5 sm:p-6 rounded-xl h-fit">
          <div className="flex items-center gap-2 mb-5">
            <div className="h-9 w-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><UserPlus className="h-5 w-5" /></div>
            <h3 className="font-heading text-lg font-semibold">Add a team member</h3>
          </div>
          <form onSubmit={addUser} className="space-y-4" data-testid="add-user-form">
            <div className="space-y-2"><Label>Full name</Label><Input value={name} onChange={(e) => setName(e.target.value)} required data-testid="new-user-name-input" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="new-user-email-input" /></div>
            <div className="space-y-2"><Label>Temporary password</Label><Input type="text" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="new-user-password-input" /></div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="new-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{roleOptions.map((r) => <SelectItem key={r} value={r} data-testid={`role-opt-${r}`}>{ROLE_META[r].label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{role === "manager" ? "Managers this manager reports to (optional)" : "Managers who see their reports"}</Label>
              <CheckList options={managerOpts} selected={managerIds} onToggle={toggleIn(setManagerIds)} testid="new-user-managers" />
            </div>
            <div className="space-y-2">
              <Label>Department</Label>
              <Select value={department || "none"} onValueChange={(v) => setDepartment(v === "none" ? "" : v)}>
                <SelectTrigger data-testid="new-user-dept-select"><SelectValue placeholder="Pick department" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {departments.map((d) => <SelectItem key={d.id} value={d.name} data-testid={`new-user-dept-${d.name}`}>{d.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" className="w-full rounded-lg h-11" disabled={saving} data-testid="add-user-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><UserPlus className="h-4 w-4 mr-1.5" /> Add member</>)}
            </Button>
          </form>
        </Card>
      )}

      <Card className={`p-5 sm:p-6 rounded-xl ${isOwner ? "lg:col-span-2" : ""}`}>
        <div className="flex items-center gap-3 mb-5">
          <h3 className="font-heading text-lg font-semibold">Team ({users.length})</h3>
          {isOwner && (
            <Button variant="outline" size="sm" className="rounded-lg ml-auto" onClick={() => setDeptDialog(true)} data-testid="manage-depts-btn">
              <Building2 className="h-4 w-4 mr-1.5" /> Departments
            </Button>
          )}
        </div>
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="space-y-2" data-testid="team-list">
            {users.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.user;
              const Icon = meta.icon;
              const isSelf = u.id === user.id;
              const canManage = !isSelf && isOwner;
              const disabled = u.is_active === false;
              return (
                <div key={u.id} className={`flex flex-wrap items-center gap-3 p-3 rounded-lg border border-border hover:bg-secondary/50 transition-colors ${disabled ? "opacity-60" : ""}`} data-testid={`team-row-${u.id}`}>
                  <Avatar src={u.avatar} name={u.name} className="h-10 w-10 text-sm shrink-0" />
                  <div className="flex-1 min-w-[200px]">
                    <p className="font-semibold leading-tight truncate">{u.name}{isSelf && " · You"}</p>
                    <p className="text-xs text-muted-foreground font-mono truncate">{u.email}</p>
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
                      <span data-testid={`managers-of-${u.id}`}>Reports to: <span className="text-foreground/70">{(u.manager_names || []).join(", ") || "Unassigned"}</span></span>
                      <span>Signed in: <span className="text-foreground/70">{fmtDate(u.last_login)}</span></span>
                      <span>Last report: <span className="text-foreground/70">{fmtDate(u.last_report_date)}</span></span>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                  <Badge className={`rounded-full border-0 gap-1 ${meta.cls}`} data-testid={`role-badge-${u.id}`}><Icon className="h-3 w-3" /> {meta.label}</Badge>
                  {isOwner && (
                    <Select value={u.department || "none"} onValueChange={(v) => setDept(u.id, v)}>
                      <SelectTrigger className="w-[130px] h-9 rounded-lg" data-testid={`dept-select-${u.id}`}><SelectValue placeholder="Dept" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No dept</SelectItem>
                        {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  {disabled && <Badge variant="outline" className="rounded-full text-[11px] text-muted-foreground border-muted-foreground/30" data-testid={`disabled-badge-${u.id}`}>Disabled</Badge>}
                  {canManage && (
                    <Button variant="outline" size="sm" className="rounded-lg h-9" data-testid={`assign-btn-${u.id}`}
                      onClick={() => setEditUser({ id: u.id, name: u.name, role: u.role, manager_ids: [...(u.manager_ids || [])] })}>
                      <Network className="h-4 w-4 mr-1" /> Assign
                    </Button>
                  )}
                  {canManage && u.role === "user" && (
                    <Button variant="outline" size="sm" className="rounded-lg h-9" data-testid={`viewees-btn-${u.id}`}
                      onClick={() => setVieweeTarget({ id: u.id, name: u.name, viewee_ids: [...(u.report_viewees || [])] })}>
                      <Eye className="h-4 w-4 mr-1" /> Members{(u.report_viewees || []).length > 0 ? ` (${u.report_viewees.length})` : ""}
                    </Button>
                  )}
                  {canManage && (
                    <Select value={u.role} onValueChange={(v) => changeRole(u.id, v, u.name)}>
                      <SelectTrigger className="w-[130px] h-9 rounded-lg" data-testid={`role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{["user", "manager"].map((r) => <SelectItem key={r} value={r} data-testid={`set-${r}-${u.id}`}>{ROLE_META[r].label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary shrink-0" onClick={() => resetPassword(u.id, u.name)} data-testid={`reset-user-${u.id}`} title="Reset password"><KeyRound className="h-4 w-4" /></Button>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-primary shrink-0" onClick={() => { setRenameTarget(u.id); setRenameVal(u.name); }} data-testid={`rename-user-${u.id}`} title="Rename"><Pencil className="h-4 w-4" /></Button>
                  )}
                  {canManage && (
                    <Button variant="ghost" size="icon" className={`shrink-0 ${disabled ? "text-emerald-600 hover:text-emerald-700" : "text-muted-foreground hover:text-amber-600"}`} onClick={() => toggleActive(u.id, u.name, disabled)} data-testid={`toggle-active-${u.id}`} title={disabled ? "Re-enable" : "Disable"}><Power className="h-4 w-4" /></Button>
                  )}
                  {canManage && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`delete-user-${u.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Remove {u.name}?</AlertDialogTitle><AlertDialogDescription>This deletes their account and reports. Cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel><AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => removeUser(u.id, u.name)} data-testid="confirm-delete-btn">Remove</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Assign dialog */}
      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader><DialogTitle>Assign {editUser?.name}</DialogTitle><DialogDescription>Choose the managers who can see this person's reports.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">
            <Label>{assignLabel}</Label>
            <CheckList
              options={managerOpts.filter((o) => o.id !== editUser?.id)}
              selected={editUser?.manager_ids || []}
              onToggle={(id) => setEditUser((p) => ({ ...p, manager_ids: p.manager_ids.includes(id) ? p.manager_ids.filter((x) => x !== id) : [...p.manager_ids, id] }))}
              testid="edit-managers"
            />
          </div>
          <DialogFooter><Button onClick={saveAssign} className="rounded-lg" data-testid="save-assign-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage departments */}
      <Dialog open={deptDialog} onOpenChange={setDeptDialog}>
        <DialogContent data-testid="dept-dialog">
          <DialogHeader>
            <DialogTitle>Departments</DialogTitle>
            <DialogDescription>Add or remove departments. Members in a department see tickets raised to it and can assign them.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input value={newDept} onChange={(e) => setNewDept(e.target.value)} placeholder="e.g. Facility & IT" data-testid="new-dept-input"
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveDept(); } }} />
            <Button className="rounded-lg shrink-0" onClick={saveDept} data-testid="add-dept-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-60 overflow-auto" data-testid="dept-list">
            {departments.length === 0 && <p className="text-sm text-muted-foreground py-3 text-center">No departments yet.</p>}
            {departments.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`dept-row-${d.name}`}>
                <span className="text-sm font-medium">{d.name}</span>
                <button className="text-muted-foreground hover:text-destructive" onClick={() => removeDept(d.id)} data-testid={`dept-delete-${d.name}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign members a user can view (report viewers / leads) */}
      <Dialog open={!!vieweeTarget} onOpenChange={(o) => !o && setVieweeTarget(null)}>
        <DialogContent data-testid="viewees-dialog">
          <DialogHeader><DialogTitle>Members {vieweeTarget?.name} can view</DialogTitle><DialogDescription>Pick the members whose reports, plans and leave this person can see — like a manager, but only for those assigned. They stay a regular user.</DialogDescription></DialogHeader>
          <PeopleCheckList
            people={users.filter((o) => o.id !== vieweeTarget?.id).map((o) => ({ id: o.id, name: o.name }))}
            selected={vieweeTarget?.viewee_ids || []}
            onToggle={(id) => setVieweeTarget((p) => ({ ...p, viewee_ids: p.viewee_ids.includes(id) ? p.viewee_ids.filter((x) => x !== id) : [...p.viewee_ids, id] }))}
            testid="viewees-picker"
            max="max-h-60"
          />
          <DialogFooter><Button onClick={saveViewees} disabled={savingViewees} className="rounded-lg" data-testid="save-viewees-btn">{savingViewees ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="rename-dialog">
          <DialogHeader><DialogTitle>Rename member</DialogTitle><DialogDescription>Update this person's display name across the app.</DialogDescription></DialogHeader>
          <div className="space-y-2"><Label>Full name</Label><Input value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid="rename-input" /></div>
          <DialogFooter><Button onClick={saveRename} className="rounded-lg" data-testid="save-rename-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetInfo} onOpenChange={(o) => !o && setResetInfo(null)}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>New temporary password</DialogTitle><DialogDescription>Share this with {resetInfo?.name}. They'll set their own password next sign-in.</DialogDescription></DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-secondary rounded-lg px-3 py-2.5 font-mono text-sm break-all" data-testid="temp-password-value">{resetInfo?.password}</code>
            <Button variant="secondary" size="icon" className="shrink-0" onClick={() => { navigator.clipboard?.writeText(resetInfo.password); toast.success("Copied"); }} data-testid="copy-temp-password-btn"><Copy className="h-4 w-4" /></Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
